import { compareIdentifiers } from './compare-identifiers.function';

const MAX_SAFE_INTEGER = Number.MAX_SAFE_INTEGER || /* istanbul ignore next */ 9007199254740991;

const mainVersion = `(0|[1-9]\\d*)\\.(0|[1-9]\\d*)\\.(0|[1-9]\\d*)`;
const preRelease = `(?:-((?:\\d*[a-zA-Z-][a-zA-Z0-9-]*|0|[1-9]\\d*)(?:\\.(?:\\d*[a-zA-Z-][a-zA-Z0-9-]*|0|[1-9]\\d*))*))`;
const buildVersion = `(?:\\+([a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*))`;

const parted = new RegExp(`^v?${mainVersion}${preRelease}?${buildVersion}?$`);
const fullRegex =
  /^v?(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:\d*[a-zA-Z-][a-zA-Z0-9-]*|0|[1-9]\d*)(?:\.(?:\d*[a-zA-Z-][a-zA-Z0-9-]*|0|[1-9]\d*))*))?(?:\+([a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*))?$/;

export class SemanticVersion {
  public raw: string;
  public major: number;
  public minor: number;
  public patch: number;
  public prerelease: (string | number | undefined)[] = [];
  public build: string[] = [];
  public version!: string;

  //eslint-disable-next-line max-lines-per-function
  constructor(version: string | SemanticVersion) {
    if (version instanceof SemanticVersion) {
      version = version.version;
    } else {
      //noinspection SuspiciousTypeOfGuard
      if (typeof version !== 'string') {
        throw new TypeError(`Invalid version. Must be a string. Got type "${typeof version}".`);
      }
    }

    if (version.length > 256) {
      throw new TypeError(`version is longer than 256 characters`);
    }

    const m = version.trim().match(fullRegex);

    if (!m) {
      throw new TypeError(`Invalid Version: ${version}`);
    }

    this.raw = version;

    // these are actually numbers
    this.major = +m[1];
    this.minor = +m[2];
    this.patch = +m[3];

    if (this.major > MAX_SAFE_INTEGER || this.major < 0) {
      throw new TypeError('Invalid major version');
    }

    if (this.minor > MAX_SAFE_INTEGER || this.minor < 0) {
      throw new TypeError('Invalid minor version');
    }

    if (this.patch > MAX_SAFE_INTEGER || this.patch < 0) {
      throw new TypeError('Invalid patch version');
    }

    // numberify any prerelease numeric ids
    if (!m[4]) {
      this.prerelease = [];
    } else {
      this.prerelease = m[4].split('.').map((id) => {
        if (/^[0-9]+$/.test(id)) {
          const num = +id;
          if (num >= 0 && num < MAX_SAFE_INTEGER) {
            return num;
          }
        }
        return id;
      });
    }

    this.build = m[5] ? m[5].split('.') : [];
    this.format();
  }

  private format() {
    this.version = `${this.major}.${this.minor}.${this.patch}`;
    if (this.prerelease.length) {
      this.version += `-${this.prerelease.join('.')}`;
    }
    return this.version;
  }

  public toString() {
    return this.version;
  }

  public compare(other: SemanticVersion | string) {
    if (!(other instanceof SemanticVersion)) {
      //noinspection SuspiciousTypeOfGuard
      if (typeof other === 'string' && other === this.version) {
        return 0;
      }
      other = new SemanticVersion(other);
    }

    if (other.version === this.version) {
      return 0;
    }

    return this.compareMain(other) || this.comparePre(other);
  }

  public compareMain(other: SemanticVersion | string) {
    if (!(other instanceof SemanticVersion)) {
      other = new SemanticVersion(other);
    }

    if (this.major < other.major) {
      return -1;
    }
    if (this.major > other.major) {
      return 1;
    }
    if (this.minor < other.minor) {
      return -1;
    }
    if (this.minor > other.minor) {
      return 1;
    }
    if (this.patch < other.patch) {
      return -1;
    }
    if (this.patch > other.patch) {
      return 1;
    }
    return 0;
  }

  public comparePre(other: SemanticVersion | string) {
    if (!(other instanceof SemanticVersion)) {
      other = new SemanticVersion(other);
    }

    // NOT having a prerelease is > having one
    if (this.prerelease.length && !other.prerelease.length) {
      return -1;
    } else if (!this.prerelease.length && other.prerelease.length) {
      return 1;
    } else if (!this.prerelease.length && !other.prerelease.length) {
      return 0;
    }

    let i = 0;
    do {
      const a = this.prerelease[i];
      const b = other.prerelease[i];

      if (a === undefined && b === undefined) {
        return 0;
      } else if (b === undefined) {
        return 1;
      } else if (a === undefined) {
        return -1;
      } else if (a === b) {
        //noinspection UnnecessaryContinueJS
        continue;
      } else {
        return compareIdentifiers(a, b);
      }
    } while (++i);
    throw Error('unreached');
  }
}
